import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ApiObjectService } from 'src/app/Services/api-object.service';

export interface ApiResponseData {
  command: {
    type: string;
    name: string;
    accepts: string[];
  };
  status: string;
  time: string;
  headers: { [key: string]: string };
  cookies: any[];
  body: any;
  size: string;
  apiObjectId?: string;
  apiObjectName?: string;
}

type TabType = 'body' | 'headers' | 'cookies' | 'status';
type BodyFormatType = 'pretty' | 'raw' | 'xml' | 'html' | 'js' | 'base64' | 'hex';

@Component({
  selector: 'app-api-response',
  templateUrl: './api-response.component.html',
  styleUrls: ['./api-response.component.scss']
})
export class ApiResponseComponent implements OnInit {
  activeTab: TabType = 'body';
  bodyFormat: BodyFormatType = 'pretty';

  // Response validation template generator
  showTemplateGenerator = false;
  generatedTemplate = '';
  templateCopied = false;
  savingTemplate = false;
  templateSaved = false;
  templateSaveError = '';

  constructor(
    public dialogRef: MatDialogRef<ApiResponseComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ApiResponseData,
    private apiObjectService: ApiObjectService
  ) {}

  ngOnInit(): void {
    const body = this.getBodyString();
    if (this.isXmlContent(body)) {
      this.bodyFormat = 'xml';
    } else if (this.isJsonContent(body)) {
      this.bodyFormat = 'pretty';
    } else if (this.isHtmlContent(body)) {
      this.bodyFormat = 'html';
    } else if (this.isJavaScriptContent(body)) {
      this.bodyFormat = 'js';
    } else {
      this.bodyFormat = 'raw';
    }
  }

  setActiveTab(tab: string): void {
    this.activeTab = tab as TabType;
  }

  setBodyFormat(format: BodyFormatType): void {
    this.bodyFormat = format;
  }

  onClose(): void {
    this.dialogRef.close();
  }

  getBodyString(): string {
    if (typeof this.data.body === 'string') {
      return this.data.body;
    }
    return JSON.stringify(this.data.body);
  }

  isJsonContent(value: string): boolean {
    try {
      JSON.parse(value);
      return true;
    } catch {
      return false;
    }
  }

  isXmlContent(value: string): boolean {
    if (!value) return false;
    const trimmed = value.trim();
    return trimmed.startsWith('<') && trimmed.includes('>') && trimmed.includes('</');
  }

  isHtmlContent(value: string): boolean {
    return /<html[\s\S]*?>/.test(value) || /<\/?[a-z][\s\S]*?>/i.test(value);
  }

  isJavaScriptContent(value: string): boolean {
    return /function\s+|=>|\b(const|let|var)\b/.test(value);
  }

  hasHeaders(): boolean {
    return this.data.headers && Object.keys(this.data.headers).length > 0;
  }

  hasCookies(): boolean {
    return this.data.cookies && this.data.cookies.length > 0;
  }

  formatJson(value: string): string {
    try {
      const parsed = JSON.parse(value);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return value;
    }
  }

  formatXml(value: string): string {
    try {
      let formatted = '';
      let indent = 0;
      const tab = '  ';
      const parts = value.split(/(<[^>]*>)/);
      parts.forEach(part => {
        if (part.trim() === '') return;
        if (part.startsWith('</')) {
          indent--;
          formatted += tab.repeat(Math.max(0, indent)) + part + '\n';
        } else if (part.startsWith('<') && !part.endsWith('/>')) {
          formatted += tab.repeat(indent) + part + '\n';
          indent++;
        } else if (part.startsWith('<') && part.endsWith('/>')) {
          formatted += tab.repeat(indent) + part + '\n';
        } else {
          formatted += tab.repeat(indent) + part.trim() + '\n';
        }
      });
      return formatted.trim();
    } catch {
      return value;
    }
  }

  formatHtml(value: string): string {
    return value;
  }

  formatJs(value: string): string {
    return value;
  }

  encodeToBase64(value: string): string {
    try {
      return btoa(value);
    } catch {
      return 'Unable to encode as Base64';
    }
  }

  decodeFromBase64(value: string): string {
    try {
      if (this.isBase64String(value)) {
        return atob(value);
      } else {
        return 'Content is not Base64 encoded';
      }
    } catch {
      return 'Invalid Base64 string';
    }
  }
  
  isBase64String(value: string): boolean {
    if (!value || value.length === 0) return false;
    const base64Pattern = /^[A-Za-z0-9+/]*={0,2}$/;
    if (!base64Pattern.test(value)) return false;
    
    if (value.length % 4 !== 0) return false;
    
    try {
      atob(value);
      return true;
    } catch {
      return false;
    }
  }
  formatAsBase64(value: string): string {
    return this.encodeToBase64(value);
  }
  
  getBase64Content(value: string): string {
    if (this.isBase64String(value.trim())) {
      return `Original Base64:\n${value}\n\nDecoded:\n${this.decodeFromBase64(value)}`;
    } else {
      return `${this.formatAsBase64(value)}`;
    }
  }

  formatHex(value: string): string {
    return Array.from(value)
      .map(c => c.charCodeAt(0).toString(16).padStart(2, '0'))
      .join(' ');
  }

  getHeaders(): Array<{ key: string; value: string }> {
    if (!this.data.headers) return [];
    return Object.entries(this.data.headers).map(([key, value]) => ({
      key,
      value: String(value)
    }));
  }

  getStatusCode(): number {
    const statusMatch = this.data.status.match(/(\d+)/);
    return statusMatch ? parseInt(statusMatch[1], 10) : 0;
  }

  getStatusClass(): string {
    const status = this.getStatusCode();
    if (status >= 200 && status < 300) return 'success';
    if (status >= 400) return 'error';
    if (status >= 300) return 'warning';
    return '';
  }

  getStatusText(): string {
    const status = this.getStatusCode();
    const statusTexts: { [key: number]: string } = {
      200: 'OK',
      201: 'Created',
      204: 'No Content',
      400: 'Bad Request',
      401: 'Unauthorized',
      403: 'Forbidden',
      404: 'Not Found',
      500: 'Internal Server Error'
    };
    return statusTexts[status] || 'Unknown';
  }

  formatSize(sizeStr: string): string {
    const match = sizeStr.match(/(\d+)/);
    if (!match) return sizeStr;
    const bytes = parseInt(match[1], 10);
    if (bytes === 0) return '0 B';

    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  getTabCountText(tab: string): string {
    switch (tab) {
      case 'headers':
        return this.hasHeaders() ? `(${Object.keys(this.data.headers).length})` : '';
      case 'cookies':
        return this.hasCookies() ? `(${this.data.cookies.length})` : '(0)';
      default:
        return '';
    }
  }

  // ===== Response validation template generator =====
  // Walks the captured body and produces a starter template for the
  // "validate response template" / "verify response matches template"
  // commands. Every leaf field
  // defaults to NOT_NULL (a safe, immediately-useful default, since a
  // template pre-filled with the exact captured values would break on the
  // very next run against any non-deterministic field).
  //
  // Non-empty arrays recurse into their first element only, wrapped as a
  // single-item array -- this matches how the matching engine actually
  // validates arrays (positionally, one template item per checked index),
  // so a one-item template checks index 0 as a representative sample
  // without dumping every element of a possibly-long array into the
  // generated template. An empty array can't offer a sample to recurse
  // into, so it stays NOT_EMPTY.
  private buildTemplateFromValue(value: any): any {
    if (value === null || value === undefined) {
      return 'NOT_NULL';
    }
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return 'NOT_EMPTY';
      }
      return [this.buildTemplateFromValue(value[0])];
    }
    if (typeof value === 'object') {
      const result: { [key: string]: any } = {};
      for (const key of Object.keys(value)) {
        result[key] = this.buildTemplateFromValue(value[key]);
      }
      return result;
    }
    return 'NOT_NULL';
  }

  canGenerateTemplate(): boolean {
    return this.isJsonContent(this.getBodyString());
  }

  openTemplateGenerator(): void {
    try {
      const parsedBody = JSON.parse(this.getBodyString());
      const template = this.buildTemplateFromValue(parsedBody);
      this.generatedTemplate = JSON.stringify(template, null, 2);
    } catch {
      this.generatedTemplate = '';
    }
    this.templateCopied = false;
    this.showTemplateGenerator = true;
  }

  closeTemplateGenerator(): void {
    this.showTemplateGenerator = false;
  }

  copyGeneratedTemplate(): void {
    if (!this.generatedTemplate || !navigator.clipboard) {
      return;
    }
    navigator.clipboard.writeText(this.generatedTemplate).then(() => {
      this.templateCopied = true;
      setTimeout(() => (this.templateCopied = false), 2000);
    });
  }

  // Persists the edited template onto the API Object that produced this
  // response, so it's reusable next time without regenerating/re-pasting.
  canSaveTemplateToApiObject(): boolean {
    return !!this.data.apiObjectId;
  }

  saveTemplateToApiObject(): void {
    if (!this.canSaveTemplateToApiObject() || !this.generatedTemplate) {
      return;
    }
    this.savingTemplate = true;
    this.templateSaveError = '';
    this.apiObjectService.updateValidationTemplate(this.data.apiObjectId, this.generatedTemplate).subscribe(
      () => {
        this.savingTemplate = false;
        this.templateSaved = true;
        setTimeout(() => (this.templateSaved = false), 2000);
      },
      (error) => {
        this.savingTemplate = false;
        this.templateSaveError = 'Failed to save to API Object';
        console.error('Failed to save validation template to API Object:', error);
      }
    );
  }
}